import React, { FC } from "react";
import { Helmet } from "react-helmet";

// Componente SEO reutilizable: centraliza title, description, canonical, robots, Open Graph,
// Twitter Card y datos estructurados (JSON-LD) por página. Optimizado para posicionar
// "salas privadas de karaoke en Barcelona".

const SITE_NAME = "Karaoke Rooms Barcelona";
const BASE_URL = "https://www.karaokeroomsbarcelona.com";
// Imagen social por defecto (foto de portada del local, servida de forma estable).
const DEFAULT_IMAGE = "https://appreservationback.vercel.app/themesEmails/img/portada.jpg";

export interface SeoProps {
  title: string;
  description: string;
  path?: string; // ruta canónica, p. ej. "/rooms"
  image?: string;
  noindex?: boolean;
  keywords?: string;
  jsonLd?: object | object[];
}

const Seo: FC<SeoProps> = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
  keywords,
  jsonLd,
}) => {
  const url = `${BASE_URL}${path}`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang="es" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph (Facebook, WhatsApp, LinkedIn…) */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Datos estructurados */}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(b)}
        </script>
      ))}
    </Helmet>
  );
};

// Datos estructurados del negocio local (mejora el SEO local en Barcelona). Reutilizable en Home.
export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "EntertainmentBusiness",
  name: "Karaoke Rooms Barcelona",
  description:
    "Salas privadas de karaoke en Barcelona con más de 30.000 canciones, pantallas táctiles y micrófonos inalámbricos. Reserva online.",
  url: BASE_URL,
  image: DEFAULT_IMAGE,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Barcelona",
    addressCountry: "ES",
  },
  areaServed: "Barcelona",
  priceRange: "€€",
};

export default Seo;
